import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { Home, Briefcase, Building2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import YourAnalyticsWidget from "./YourAnalyticsWidget";
import { useAuth } from "../../../context/AuthContext";

export default function Sidebar() {
  const { user } = useAuth();


  const { data: analytics } = useQuery({
    queryKey: ["dashboard", "analytics"],
    queryFn: () => backendApi.entities.Dashboard.analytics(),
    staleTime: 60000,
    enabled: !!backendApi.auth.getToken(),
  });
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { label: "My home", icon: Home, path: "/jobs/dashboard" },
    { label: "Jobs", icon: Briefcase, path: "/jobs/list" },
    { label: "Companies", icon: Building2, path: "/jobs/companies" },
    { label: "Blogs", icon: BookOpen, path: "/jobs/blogs" },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      {/* Profile Section */}
      <div className="relative pt-8 pb-6 px-6 flex flex-col items-center text-center border-b border-gray-100">
        <div className="relative mb-3">

          {/* Profile progress circle */}
          <div className="w-20 h-20 relative flex items-center justify-center">
            {/* SVG Circular Progress */}
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="44"
                stroke="currentColor"
                strokeWidth="6"
                fill="transparent"
                className="text-gray-100"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="44"
                stroke="currentColor"
                strokeWidth="6"
                fill="transparent"
                strokeDasharray="276.46"
                strokeDashoffset={276.46 * (1 - (analytics?.profileCompletion || 0) / 100)}
                strokeLinecap="round"
                className="text-green-500 transition-all duration-1000 ease-out"
              />
            </svg>

            {/* USER INITIAL */}
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-3xl font-bold text-indigo-700 relative z-10">
              {(user?.name || user?.full_name || "U").charAt(0).toUpperCase()}
            </div>
          </div>

          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white px-1">
            <span className="text-xs font-bold text-gray-700">{analytics?.profileCompletion || 0}%</span>
          </div>
        </div>

        {/* Name */}
        <h3 className="font-bold text-gray-900 text-lg">{user?.name || user?.full_name || ""}</h3>

        {/* ⭐ CLICK → OPEN PROFILE PAGE */}
        <Link to="/jobs/profile" className="w-full">
          <Button className="w-full mt-4 bg-[#2557a7] hover:bg-[#1e4687] text-white rounded-full h-8 text-sm font-medium">
            Complete profile
          </Button>
        </Link>
      </div>

      {/* Profile Performance */}
      <div className="p-4 bg-blue-50/50 border-b border-gray-100 mx-2 my-2 rounded-lg">
        <div className="flex justify-between mb-2">
          <span className="text-xs font-bold text-gray-700">Profile performance</span>
          <span className="text-gray-400 text-xs cursor-pointer">ⓘ</span>
        </div>

        <div className="flex justify-between">
          <div>
            <p className="text-[10px] text-gray-500">Search<br />appearances</p>
            <p className="text-blue-600 font-bold text-sm mt-1">
              {analytics?.recruiterSearches || 0} <span className="text-gray-400 font-normal text-xs">&gt;</span>
            </p>
          </div>

          <div className="h-8 w-[1px] bg-gray-200 mb-1" />

          <div>
            <p className="text-[10px] text-gray-500">Recruiter<br />actions</p>
            <p className="text-blue-600 font-bold text-sm mt-1">
              {analytics?.profileViews || 0} <span className="text-gray-400 font-normal text-xs">&gt;</span>
            </p>
          </div>
        </div>
      </div>

      {/* Your Analytics widget nested directly into sidebar */}
      <div className="mx-2 my-4">
        <YourAnalyticsWidget />
      </div>

      {/* Navigation */}
      <div className="py-2">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex items-center gap-3 px-6 py-3 cursor-pointer transition-all border-l-4 ${isActive
                ? "bg-blue-50 text-[#2557a7] border-[#2557a7] font-bold"
                : "text-gray-600 hover:bg-gray-50 border-transparent font-medium"
                } text-sm`}
            >
              <Icon className="w-5 h-5" />
              {item.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}
